import type { Notification } from '@notify-x/shared';
import { formatRelativeTime } from '../utils/date-formatter.js';

/**
 * Get icon for event type
 */
function getEventIcon(eventType: string): string {
	const iconMap: Record<string, string> = {
		'pr:created': '🆕',
		'pr:updated': '✏️',
		'pr:merged': '✅',
		'pr:declined': '❌',
		'pr:approved': '👍',
		'pr:unapproved': '👎',
		'pr:comment:created': '💬',
		'pr:comment:updated': '📝',
		'pr:comment:deleted': '🗑️',
		mention: '👤',
	};

	return iconMap[eventType] || '📌';
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
	const div = document.createElement('div');
	div.textContent = text;
	return div.innerHTML;
}

/**
 * Render a single notification item
 */
export function renderNotificationItem(
	notification: Notification,
	onClick: (id: number) => void,
): string {
	const timeAgo = formatRelativeTime(notification.created_at);
	const icon = getEventIcon(notification.event_type);

	return `
		<div
			class="notification-item"
			data-notification-id="${notification.id}"
			role="button"
			tabindex="0"
		>
			<div class="notification-icon">${icon}</div>
			<div class="notification-content">
				<div class="notification-header">
					<h3 class="notification-title">${escapeHtml(notification.title)}</h3>
				</div>
				<p class="notification-message">${escapeHtml(notification.message)}</p>
				<div class="notification-meta">
					<span class="notification-repo">📦 ${escapeHtml(notification.repository_name)}</span>
					<span class="notification-time">🕐 ${timeAgo}</span>
				</div>
			</div>
		</div>
	`;
}

/**
 * Attach click handler to notification items
 */
export function attachNotificationClickHandlers(
	container: HTMLElement,
	onClick: (id: number) => void,
): void {
	const items = container.querySelectorAll('.notification-item');

	for (const item of items) {
		const notificationId = Number.parseInt(item.getAttribute('data-notification-id') || '0', 10);

		if (notificationId > 0) {
			// Click handler
			item.addEventListener('click', () => onClick(notificationId));

			// Keyboard handler for accessibility
			item.addEventListener('keypress', (e) => {
				if (e instanceof KeyboardEvent && (e.key === 'Enter' || e.key === ' ')) {
					e.preventDefault();
					onClick(notificationId);
				}
			});
		}
	}
}
